import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (error) {
          setError(errorDescription || error);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        if (!code) {
          setError('No authorization code received');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Check if we're being called as part of the OAuth redirect
        // If so, the backend has already processed the callback
        if (window.location.pathname === '/auth/callback') {
          // Try to get the response from the current page if it's JSON
          try {
            const pageContent = document.body.textContent;
            if (pageContent && pageContent.trim().startsWith('{')) {
              const data = JSON.parse(pageContent.trim());
              if (data.success && data.token && data.user) {
                login(data.token, data.user);
                navigate('/admin');
                return;
              }
            }
          } catch (e) {
            // If parsing fails, fall back to making an API call
          }
        }

        // Fallback: make an API call to the callback endpoint
        const response = await fetch(`/auth/callback${window.location.search}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(errorData || 'Authentication failed');
        }

        const data = await response.json();

        if (data.success && data.token && data.user) {
          login(data.token, data.user);
          navigate('/admin');
        } else {
          throw new Error('Invalid response from authentication server');
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setError(error instanceof Error ? error.message : 'Authentication failed');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [login, navigate]);

  if (error) {
    return (
      <div className="oauth-callback-container">
        <div className="oauth-callback-card error">
          <h2>Authentication Failed</h2>
          <p>{error}</p>
          <p>Redirecting to login page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="oauth-callback-container">
      <div className="oauth-callback-card">
        <div className="spinner"></div>
        <h2>Signing you in...</h2>
        <p>Please wait while we complete your authentication.</p>
      </div>
    </div>
  );
};